import {
  ApiClient,
  type ApiClientOptions,
  type CursorPagedResponse,
} from '@/infra/api-client'
import type {
  PublishedLegendResource,
  ListPublishedLegendRequest,
  LegendSettingsResource,
  FetchLegendSettingsRequest,
  GetManyLegendsRequest,
} from './resources/legend'
import { toURLSearchParams } from '@/utils/url-util'

const SUPPORTED_ENGINE_VERSION = '1.39.2'
const supportedEngineVersionParams = {
  supported_engine_version: SUPPORTED_ENGINE_VERSION,
} as const

export class LegendsApi {
  readonly publishedLegends
  readonly legendSettings

  constructor(options: ApiClientOptions) {
    this.publishedLegends = new PublishedLegendApi(options)
    this.legendSettings = new LegendSettingsApi(options)
  }
}

export class PublishedLegendApi extends ApiClient {
  /**
   * Lists published legends.
   */
  list(
    { workspace_id: workspaceId, ...request }: ListPublishedLegendRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<PublishedLegendResource>> {
    const params = toURLSearchParams(
      { ...request, ...supportedEngineVersionParams },
      { arrayStyle: 'multi-key-bracket' },
    )

    return this._get(`v3/workspaces/${workspaceId}/legends`, params, {
      signal,
    })
  }

  /**
   * Get a legend by ID.
   *
   * @param id
   * @param signal
   */
  get(id: string, signal?: AbortSignal): Promise<PublishedLegendResource> {
    return this._get(
      `v3/legends/${encodeURIComponent(id)}`,
      toURLSearchParams(supportedEngineVersionParams),
      { signal },
    )
  }

  /**
   * Gets multiple legends by IDs.
   * NOTE: This requires admin permission. Leaving it here for now in case
   * that changes.
   *
   * @param request
   * @param signal
   */
  getMany(
    request: GetManyLegendsRequest,
    signal?: AbortSignal,
  ): Promise<(PublishedLegendResource | null)[]> {
    return this._post(
      `v3/legends/id-query`,
      request,
      toURLSearchParams(supportedEngineVersionParams),
      { signal },
    )
  }
}

export class LegendSettingsApi extends ApiClient {
  /**
   * Gets the settings for a legend.
   */
  getMany(
    payload: readonly FetchLegendSettingsRequest[],
  ): Promise<LegendSettingsResource[]> {
    return this._post(
      `v3/legends/id-query/settings`,
      payload,
      toURLSearchParams(supportedEngineVersionParams),
    )
  }
}
